import { Fragment, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import {
    clawBackMpesaFeeVariance,
    getPendingMpesaFeeVariances,
    writeOffMpesaFeeVariance,
} from '../../services/LedgerService'
import Pagination from './Pagination'

const QUERY_KEY = 'pending-mpesa-fee-variances'

const ACTIONS = {
    CLAW_BACK: 'claw-back',
    WRITE_OFF: 'write-off',
}

const formatAmount = (amount) =>
    amount == null
        ? null
        : `KES ${Number(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`

const formatDate = (dateString) =>
    new Date(dateString).toLocaleString('en-KE', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })

const canClawBack = (walletBalance, shortfall) =>
    walletBalance != null && Number(walletBalance) >= Number(shortfall)

const UserLabel = ({ user }) => {
    if (!user) return <span className="text-muted fst-italic">Unknown</span>
    return (
        <>
            {user.name}
            {user.is_deleted && (
                <span className="text-muted ms-1" style={{ fontSize: '0.75rem' }}>(deleted)</span>
            )}
        </>
    )
}

const WalletBalanceLabel = ({ balance, shortfall }) => {
    const formatted = formatAmount(balance)
    if (formatted == null) {
        return <span className="text-muted fst-italic">No wallet</span>
    }
    const enough = canClawBack(balance, shortfall)
    return (
        <span className={enough ? 'text-success' : 'text-warning'} title={enough ? 'Clawback would succeed today' : 'Wallet is short of the shortfall'}>
            {formatted}
        </span>
    )
}

const RowActions = ({ row, onClawBack, onWriteOff }) => {
    const enabled = canClawBack(row.payout?.user?.wallet_balance, row.shortfall)
    return (
        <>
            <button
                type="button"
                className="btn btn-sm btn-primary me-2"
                onClick={() => onClawBack(row)}
                disabled={!enabled}
                title={enabled ? undefined : 'The wallet balance is short of the shortfall. Wait for a top-up or write off.'}
            >
                Claw back
            </button>
            <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => onWriteOff(row)}
            >
                Write off
            </button>
        </>
    )
}

const MpesaFeeVarianceTab = () => {
    const queryClient = useQueryClient()

    const [page, setPage]         = useState(1)
    const [action, setAction]     = useState(null)
    const [selected, setSelected] = useState(null)
    const [notes, setNotes]       = useState('')

    const { status, data, error } = useQuery({
        queryKey: [QUERY_KEY, page],
        queryFn:  () => getPendingMpesaFeeVariances(page),
        placeholderData: keepPreviousData,
        staleTime: 0,
    })

    const rows     = data?.data?.data ?? []
    const lastPage = data?.data?.meta?.last_page ?? 1

    const mutation = useMutation({
        mutationFn: ({ id, actionKind, payload }) =>
            actionKind === ACTIONS.CLAW_BACK
                ? clawBackMpesaFeeVariance(id, payload)
                : writeOffMpesaFeeVariance(id, payload),
        onSuccess: async (_response, variables) => {
            toast.success(variables.actionKind === ACTIONS.CLAW_BACK
                ? 'Shortfall clawed back from the user\'s wallet.'
                : 'Variance written off to the platform.')
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
            handleClose()
        },
        onError: (err) => {
            const message = err?.response?.data?.message || 'Failed to resolve the variance. Please try again.'
            toast.error(message)
        },
    })

    const openClawBack = (row) => { setSelected(row); setAction(ACTIONS.CLAW_BACK); setNotes('') }
    const openWriteOff = (row) => { setSelected(row); setAction(ACTIONS.WRITE_OFF); setNotes('') }

    const handleClose = () => {
        if (mutation.isPending) return
        setSelected(null)
        setAction(null)
        setNotes('')
    }

    const handleSubmit = (event) => {
        event.preventDefault()
        mutation.mutate({
            id:         selected.id,
            actionKind: action,
            payload:    notes.trim() === '' ? {} : { resolution_notes: notes.trim() },
        })
    }

    const isClawBack = action === ACTIONS.CLAW_BACK
    const isWriteOff = action === ACTIONS.WRITE_OFF
    const notesValid = isWriteOff ? notes.trim().length >= 3 : true

    const helper = (
        <p className="text-muted small mb-3 px-3 px-sm-0">
            Payouts where Safaricom charged more than was reserved and the wallet was too empty to self-heal at settlement. Claw the shortfall back once the wallet is topped up, or write it off to the platform.
        </p>
    )

    if (status === 'pending') {
        return (
            <>
                {helper}
                <div className="d-flex justify-content-center py-4">
                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                </div>
            </>
        )
    }

    if (error) {
        return (
            <>
                {helper}
                <p className="text-center text-danger py-4 mb-0">
                    Failed to load M-PESA fee variances. Please try again.
                </p>
            </>
        )
    }

    if (rows.length === 0) {
        return (
            <>
                {helper}
                <p className="text-center text-muted py-4 mb-0 px-3">
                    No M-PESA fee variances are pending. Every payout so far has been charged the expected fee.
                </p>
            </>
        )
    }

    return (
        <Fragment>
            {helper}

            <div className="table-responsive d-none d-xl-block px-3 pt-2">
                <table className="table verticle-middle mb-0">
                    <thead>
                        <tr>
                            <th scope="col">User</th>
                            <th scope="col">Payout Receipt</th>
                            <th scope="col" className="text-nowrap">Expected</th>
                            <th scope="col" className="text-nowrap">Actual</th>
                            <th scope="col" className="text-nowrap">Shortfall</th>
                            <th scope="col" className="text-nowrap">Wallet</th>
                            <th scope="col" className="text-nowrap">Detected</th>
                            <th scope="col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.id}>
                                <td><UserLabel user={row.payout?.user} /></td>
                                <td className="text-nowrap">{row.payout?.mpesa_receipt_number ?? '-'}</td>
                                <td className="text-nowrap">{formatAmount(row.expected_fee)}</td>
                                <td className="text-nowrap">{formatAmount(row.actual_fee)}</td>
                                <td className="text-nowrap fw-semibold">{formatAmount(row.shortfall)}</td>
                                <td className="text-nowrap">
                                    <WalletBalanceLabel balance={row.payout?.user?.wallet_balance} shortfall={row.shortfall} />
                                </td>
                                <td className="text-nowrap">{formatDate(row.created_at)}</td>
                                <td className="text-nowrap">
                                    <RowActions row={row} onClawBack={openClawBack} onWriteOff={openWriteOff} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="table-responsive d-none d-md-block d-xl-none px-3 pt-2">
                <table className="table verticle-middle mb-0">
                    <thead>
                        <tr>
                            <th scope="col">Variance</th>
                            <th scope="col">Wallet</th>
                            <th scope="col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.id}>
                                <td>
                                    <div className="fw-semibold"><UserLabel user={row.payout?.user} /></div>
                                    <div className="small text-muted">Receipt {row.payout?.mpesa_receipt_number ?? '-'}</div>
                                    <div className="small text-muted">
                                        Expected {formatAmount(row.expected_fee)}, actual {formatAmount(row.actual_fee)}
                                    </div>
                                    <div className="small mt-1">
                                        Shortfall <span className="fw-semibold">{formatAmount(row.shortfall)}</span>
                                    </div>
                                    <div className="small text-muted mt-1">
                                        <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                        {formatDate(row.created_at)}
                                    </div>
                                </td>
                                <td>
                                    <WalletBalanceLabel balance={row.payout?.user?.wallet_balance} shortfall={row.shortfall} />
                                </td>
                                <td className="text-nowrap">
                                    <RowActions row={row} onClawBack={openClawBack} onWriteOff={openWriteOff} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ul className="list-group list-group-flush d-block d-md-none">
                {rows.map((row) => (
                    <li key={row.id} className="list-group-item py-3">
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                            <span className="fw-semibold text-body"><UserLabel user={row.payout?.user} /></span>
                            <span className="badge badge-warning light ms-auto">
                                Short {formatAmount(row.shortfall)}
                            </span>
                        </div>
                        <div className="small text-muted">
                            Receipt {row.payout?.mpesa_receipt_number ?? '-'}
                        </div>
                        <div className="small text-muted">
                            Expected {formatAmount(row.expected_fee)}, actual {formatAmount(row.actual_fee)}
                        </div>
                        <div className="small text-muted mt-1">
                            Wallet <WalletBalanceLabel balance={row.payout?.user?.wallet_balance} shortfall={row.shortfall} />
                        </div>
                        <div className="small text-muted mt-1">
                            <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                            {formatDate(row.created_at)}
                        </div>
                        <div className="mt-2">
                            <RowActions row={row} onClawBack={openClawBack} onWriteOff={openWriteOff} />
                        </div>
                    </li>
                ))}
            </ul>

            <Pagination page={page} totalPages={lastPage} onPageChange={setPage} />

            <Modal show={!!selected} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isClawBack ? 'Claw back from wallet' : 'Write off to platform'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selected && (
                        <p className="text-muted mb-3">
                            {isClawBack ? (
                                <>
                                    Debit <strong>{formatAmount(selected.shortfall)}</strong> from the wallet of{' '}
                                    <strong>{selected.payout?.user?.name ?? 'this user'}</strong>. The platform's M-PESA fee expense is reversed by the same amount.
                                </>
                            ) : (
                                <>
                                    Confirm the platform absorbs the <strong>{formatAmount(selected.shortfall)}</strong> shortfall on the payout to{' '}
                                    <strong>{selected.payout?.user?.name ?? 'this user'}</strong>. No further ledger entries are written; the reason below is stored as the audit trail.
                                </>
                            )}
                        </p>
                    )}
                    <form id="resolve-mpesa-fee-variance-form" onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label className="form-label" htmlFor="resolution-notes">
                                Reason {isWriteOff && <span className="text-danger">*</span>}
                            </label>
                            <textarea
                                id="resolution-notes"
                                className="form-control"
                                rows={3}
                                maxLength={500}
                                value={notes}
                                onChange={(event) => setNotes(event.target.value)}
                                required={isWriteOff}
                                placeholder={isWriteOff ? 'Why is this being written off (e.g. confirmed Safaricom glitch)?' : 'Optional note for the audit trail.'}
                            />
                        </div>
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={mutation.isPending}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form="resolve-mpesa-fee-variance-form"
                        variant={isClawBack ? 'primary' : 'warning'}
                        disabled={mutation.isPending || !notesValid}
                    >
                        {mutation.isPending
                            ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Working</>
                            : (isClawBack ? 'Confirm clawback' : 'Confirm write-off')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    )
}

export default MpesaFeeVarianceTab
